import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Gift, XCircle, XLg, ExclamationTriangleFill, PeopleFill, PlusLg, Trophy } from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import {
  fetchActiveGiftDrops,
  fetchGiftDrops,
  createGiftDrop,
  cancelGiftDrop,
} from '../api/giftDrops';
import { connectGiftDropsSocket } from '../api/giftDropsSocket';
import { getToken } from '../config/api';
import { isEmbedded } from '../config/embedded';
import { fetchBrandProducts } from '../api/brands';
import { ProductPicker, Thumb } from './wheel/RoundsBuilder';
import FreeNotice, { FreeAccept } from './wheel/FreeNotice';
import { formatPrice, prizeCatalogue, totalValue } from './wheel/wheelProducts';
import {
  NOTE_MAX,
  PRODUCTS_MAX,
  HISTORY_PAGE_SIZE,
  EXPIRY_RECHECK_MS,
  STATUSES,
  applyGiftEvent,
  readGiftEvent,
  dropPhase,
  errorText,
  formatCountdown,
  formatReaction,
  mergeFirstPage,
  pollIntervalMs,
  sortNewestFirst,
} from './giftDropShape';
import './GiftDropsTab.scss';

// Challenges / Játékok — the brand side of "Ajándékláda a galaxisban": the
// brand drops a gift chest (its own logo on it) into the Outfino app's Style
// Universe galaxy. The chest drifts in from beyond the rim, crosses rim to rim
// (~100 s), and the first player to tap it in the app takes it; nobody → it
// drifts out. A brand has no limit of its own: any number of its chests may be
// out at once; only a full galaxy (the global cap) can refuse one.
//
// The admin panel's Challenges page (admin/src/features/challenges) is the
// model. Live via the gift:brand:<brandId> socket room in the portal; the
// Shopify embedded app (no socket for session tokens) and a dropped socket
// poll instead (giftDropShape.js).

const REFRESH_DEBOUNCE_MS = 300;

function fmtDateTime(ms, lang) {
  if (ms === null || ms === undefined) return '—';
  return new Intl.DateTimeFormat(lang === 'hu' ? 'hu-HU' : 'en-GB', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(ms));
}

function useNow(intervalMs) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!intervalMs) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

// Escape closes, unless something is in flight.
function useEscape(onClose, busy) {
  const ref = useRef(onClose);
  ref.current = onClose;
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape' && !busy) ref.current();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy]);
}

function ChestLogo({ logo, name, size = 'md' }) {
  return (
    <span className={`gift-chest gift-chest--${size}`} aria-hidden="true">
      <Gift className="gift-chest__icon" />
      <span className="gift-chest__logo">
        {logo ? <img src={logo} alt="" /> : <span>{name?.[0]?.toUpperCase() || 'B'}</span>}
      </span>
    </span>
  );
}

function WinnerCell({ drop }) {
  const { t } = useLocale();
  if (drop.status !== 'collected') return <span className="gift-muted">—</span>;
  const w = drop.winner;
  const name = !w || w.anonymous || !w.name ? t('gifts.someone') : w.name;
  const letter = (w && !w.anonymous && (w.name || w.username || '').trim().charAt(0).toUpperCase()) || '?';
  return (
    <span className="gift-person">
      <span className="gift-person__avatar" aria-hidden="true">
        <span>{letter}</span>
        {w?.avatarUrl && (
          <img src={w.avatarUrl} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        )}
      </span>
      <span className="gift-person__name">{name}</span>
      {w?.username && w.username !== name && <span className="gift-muted">@{w.username}</span>}
    </span>
  );
}

/** The chest's prize (v2.2): the products, compact. */
function PrizeLine({ drop, compact = false }) {
  const { t, lang } = useLocale();
  const products = drop.prize?.products ?? [];
  if (!products.length) return compact ? <span className="gift-muted">{t('gifts.prize.none')}</span> : null;
  return (
    <div className={`gift-prize${compact ? ' is-compact' : ''}`}>
      <span className="gift-prize__thumbs">
        {products.slice(0, 3).map((p) => <Thumb key={p.id} product={p} />)}
        {products.length > 3 && <span className="gift-prize__more">+{products.length - 3}</span>}
      </span>
      <span className="gift-prize__text">
        {!compact && <span className="gift-prize__label"><Trophy size={11} aria-hidden="true" /> {t('gifts.prize.label')}</span>}
        <span className="gift-prize__names" title={products.map((p) => p.name).join(', ')}>
          {products.map((p) => p.name).join(', ')}
        </span>
        <span className="gift-muted gift-prize__value">{totalValue(products, lang)} · {t('gifts.prize.freeShip')}</span>
      </span>
    </div>
  );
}

function FollowersBadge({ drop }) {
  const { t } = useLocale();
  if (!drop.followersOnly) return null;
  return (
    <span className="gift-followers" title={t('gifts.followersOnlyTitle')}>
      <PeopleFill size={11} aria-hidden="true" /> {t('gifts.followersOnly')}
    </span>
  );
}

function StatusBadge({ status }) {
  const { t } = useLocale();
  const known = STATUSES.includes(status);
  return (
    <span className={`gift-status is-${known ? status : 'other'}`}>
      {known ? t(`gifts.status.${status}`) : status}
    </span>
  );
}

function ActiveCard({ drop, brand, nowMs, canEdit, onCancel }) {
  const { t, lang } = useLocale();
  const { phase, remainingMs, progress } = dropPhase(drop, nowMs);
  return (
    <div className={`gift-card is-${phase}`}>
      <div className="gift-card__top">
        <ChestLogo logo={drop.source?.logoUrl || brand.logo} name={brand.name} />
        <div className="gift-card__clock">
          <div className="gift-card__time">{phase === 'leaving' ? '0:00' : formatCountdown(remainingMs)}</div>
          <div className="gift-card__phase">{t(`gifts.phase.${phase}`)}</div>
          <FollowersBadge drop={drop} />
        </div>
        {canEdit && (
          <button type="button" className="btn-o btn-o--ghost btn-o--sm gift-card__cancel" onClick={() => onCancel(drop)}>
            <XCircle size={14} /> {t('gifts.cancel')}
          </button>
        )}
      </div>
      <div
        className="gift-progress"
        role="progressbar"
        aria-label={t(`gifts.phase.${phase}`)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
      >
        <span style={{ width: `${Math.round((1 - progress) * 1000) / 10}%` }} />
      </div>
      <div className="gift-card__meta">
        <span>
          <span className="gift-muted">{t('gifts.col.createdAt')}</span> {fmtDateTime(drop.createdAt, lang)}
        </span>
        {drop.crossingMs != null && (
          <span>
            <span className="gift-muted">{t('gifts.col.crossing')}</span>{' '}
            {t('gifts.seconds', { n: Math.round(drop.crossingMs / 1000) })}
          </span>
        )}
      </div>
      <PrizeLine drop={drop} />
      {drop.note && <div className="gift-card__note">„{drop.note}”</div>}
    </div>
  );
}

function DropModal({ brand, onClose, onDone }) {
  const { t, lang } = useLocale();
  const [note, setNote] = useState('');
  const [followersOnly, setFollowersOnly] = useState(false);
  const [productIds, setProductIds] = useState([]);
  const [picking, setPicking] = useState(false);
  const [catalogue, setCatalogue] = useState(null); // null loading | product[] | 'error'
  const [freeAccepted, setFreeAccepted] = useState(false);
  const [freeMissing, setFreeMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEscape(onClose, busy);

  // The brand's own products (the Products tab's endpoint), loaded once the picker is first opened.
  useEffect(() => {
    if (!picking || catalogue !== null) return undefined;
    let alive = true;
    fetchBrandProducts(brand.id)
      .then((list) => alive && setCatalogue(prizeCatalogue(list)))
      .catch(() => alive && setCatalogue('error'));
    return () => {
      alive = false;
    };
  }, [picking, catalogue, brand.id]);

  const byId = useMemo(() => new Map(Array.isArray(catalogue) ? catalogue.map((p) => [p.id, p]) : []), [catalogue]);
  const chosen = productIds.map((id) => byId.get(id)).filter(Boolean);
  const toggle = (id) => setProductIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    if (productIds.length && !freeAccepted) {
      setFreeMissing(true);
      setError(t('gifts.err.freeShipping'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await createGiftDrop(brand.id, { note, followersOnly, productIds, freeShippingAccepted: freeAccepted });
      onDone(result);
    } catch (err) {
      setError(errorText(err, t));
      setBusy(false);
    }
  }

  return createPortal(
    <div className="gift-modal" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <form className="gift-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="gift-drop-title" onSubmit={submit}>
        <div className="gift-modal__head">
          <h3 id="gift-drop-title" className="gift-modal__title">{t('gifts.modal.title')}</h3>
          <button type="button" className="gift-modal__close" onClick={onClose} disabled={busy} aria-label={t('gifts.modal.back')}>
            <XLg size={14} aria-hidden="true" />
          </button>
        </div>

        <div className="gift-modal__preview">
          <ChestLogo logo={brand.logo} name={brand.name} size="lg" />
          <div>
            <strong>{t('gifts.modal.logoTitle')}</strong>
            <span>{t('gifts.modal.logoText')}</span>
          </div>
        </div>

        <p className="gift-modal__text">{t('gifts.modal.text')}</p>

        <div className="gift-modal__field">
          <span>{t('gifts.modal.prize')}</span>
          <small className="gift-modal__hint gift-prize__hint">{t('gifts.modal.prizeHint', { max: PRODUCTS_MAX })}</small>
          {chosen.length > 0 && (
            <ul className="wheel-round__products">
              {chosen.map((p) => (
                <li key={p.id} className="wheel-product">
                  <Thumb product={p} />
                  <span className="wheel-product__name" title={p.name}>{p.name}</span>
                  <span className="wheel-product__price">{formatPrice(p, lang)}</span>
                  <button type="button" className="wheel-product__remove" onClick={() => toggle(p.id)} disabled={busy} aria-label={t('wheel.form.productRemove', { name: p.name })}>
                    <XLg size={11} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {picking ? (
            <ProductPicker
              catalogue={catalogue}
              selected={productIds}
              onToggle={toggle}
              onClose={() => setPicking(false)}
              disabled={busy}
              max={PRODUCTS_MAX}
            />
          ) : (
            <button type="button" className="wheel-round__add gift-prize__add" onClick={() => setPicking(true)} disabled={busy}>
              <PlusLg size={12} /> {t(chosen.length ? 'wheel.form.productsEdit' : 'gifts.modal.prizeAdd')}
            </button>
          )}
        </div>

        {productIds.length > 0 && (
          <>
            <FreeNotice />
            <FreeAccept
              checked={freeAccepted}
              onChange={(v) => { setFreeAccepted(v); setFreeMissing(false); }}
              disabled={busy}
              invalid={freeMissing}
            />
          </>
        )}

        <label className={`gift-modal__toggle${followersOnly ? ' is-on' : ''}`}>
          <input
            type="checkbox"
            role="switch"
            checked={followersOnly}
            onChange={(e) => setFollowersOnly(e.target.checked)}
            disabled={busy}
          />
          <span className="gift-modal__switch" aria-hidden="true" />
          <span className="gift-modal__toggle-text">
            <strong>{t('gifts.modal.followersOnly')}</strong>
            <span>{t('gifts.modal.followersOnlyHint')}</span>
          </span>
        </label>

        <label className="gift-modal__field">
          <span>{t('gifts.modal.note')}</span>
          <textarea
            rows={2}
            maxLength={NOTE_MAX}
            value={note}
            placeholder={t('gifts.modal.notePlaceholder')}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
          />
          <small className="gift-modal__count">{note.length}/{NOTE_MAX}</small>
        </label>

        {error && <div className="gift-modal__error" role="alert">{error}</div>}

        <div className="gift-modal__actions">
          <button type="button" className="btn-o btn-o--ghost" onClick={onClose} disabled={busy}>
            {t('gifts.modal.back')}
          </button>
          <button type="submit" className="btn-o btn-o--brand" disabled={busy}>
            <Gift size={15} style={{ marginRight: 8 }} />
            {busy ? t('gifts.modal.dropping') : t('gifts.modal.confirm')}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

function CancelModal({ brand, drop, onClose, onDone }) {
  const { t, lang } = useLocale();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEscape(onClose, busy);

  async function submit() {
    setBusy(true);
    setError('');
    try {
      const updated = await cancelGiftDrop(brand.id, drop.id);
      onDone(updated ?? { ...drop, status: 'cancelled' });
    } catch (err) {
      setError(errorText(err, t));
      setBusy(false);
    }
  }

  return createPortal(
    <div className="gift-modal" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="gift-modal__dialog gift-modal__dialog--small" role="alertdialog" aria-modal="true" aria-labelledby="gift-cancel-title">
        <div className="gift-modal__head">
          <h3 id="gift-cancel-title" className="gift-modal__title">{t('gifts.cancelTitle')}</h3>
        </div>
        <p className="gift-modal__text">{t('gifts.cancelText', { at: fmtDateTime(drop.createdAt, lang) })}</p>
        {error && <div className="gift-modal__error" role="alert">{error}</div>}
        <div className="gift-modal__actions">
          <button type="button" className="btn-o btn-o--ghost" onClick={onClose} disabled={busy}>
            {t('gifts.modal.back')}
          </button>
          <button type="button" className="btn-o gift-btn--danger" onClick={submit} disabled={busy}>
            {busy ? t('gifts.cancelling') : t('gifts.cancelConfirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function GiftDropsTab({ brand, canEdit = false }) {
  const { t, lang } = useLocale();

  const [active, setActive] = useState([]);
  const [activeLoaded, setActiveLoaded] = useState(false);
  const [activeError, setActiveError] = useState(null);

  const [history, setHistory] = useState({ drops: [], nextBefore: null });
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const [dropOpen, setDropOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [notice, setNotice] = useState('');

  // Server clock − client clock, from serverNowMs (midpoint of the request).
  const offsetRef = useRef(0);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const loadActive = useCallback(async () => {
    try {
      const sentAt = Date.now();
      const list = await fetchActiveGiftDrops(brand.id);
      if (!alive.current) return;
      if (list.serverNowMs !== null) {
        offsetRef.current = list.serverNowMs - Math.round((sentAt + Date.now()) / 2);
      }
      setActive(list.drops.filter((d) => d.status === 'active'));
      setActiveError(null);
    } catch (err) {
      if (alive.current) setActiveError(err);
    } finally {
      if (alive.current) setActiveLoaded(true);
    }
  }, [brand.id]);

  const loadHistory = useCallback(async () => {
    try {
      const list = await fetchGiftDrops(brand.id, { limit: HISTORY_PAGE_SIZE });
      if (!alive.current) return;
      setHistory((prev) => ({
        drops: mergeFirstPage(prev.drops, list.drops),
        // Keep the cursor of the pages loaded further down, if any.
        nextBefore: prev.drops.length > list.drops.length ? prev.nextBefore : list.nextBefore,
      }));
      setHistoryError(null);
    } catch (err) {
      if (alive.current) setHistoryError(err);
    } finally {
      if (alive.current) setHistoryLoaded(true);
    }
  }, [brand.id]);

  const refresh = useCallback(() => Promise.all([loadActive(), loadHistory()]), [loadActive, loadHistory]);

  useEffect(() => { refresh(); }, [refresh]);

  const refreshTimer = useRef(null);
  const scheduleRefresh = useCallback((delay = REFRESH_DEBOUNCE_MS) => {
    clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(refresh, delay);
  }, [refresh]);
  useEffect(() => () => clearTimeout(refreshTimer.current), []);

  // Live: the brand's room on /style-universe (portal only). Each event is
  // applied at once, then the lists are read again — the server stays the
  // source of truth.
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    if (isEmbedded()) return undefined;
    const conn = connectGiftDropsSocket({
      token: getToken(),
      brandId: brand.id,
      onConnectionChanged: (up) => {
        if (!alive.current) return;
        setConnected(up);
        if (up) scheduleRefresh(0);
      },
      onEvent: (name, data) => {
        if (!alive.current) return;
        const event = readGiftEvent(name, data);
        if (event?.kind === 'state' && event.serverNowMs !== null) {
          offsetRef.current = event.serverNowMs - Date.now();
        }
        if (event) {
          setActive((prev) => applyGiftEvent(prev, event).filter((d) => d.status === 'active'));
          setHistory((prev) => ({ ...prev, drops: applyGiftEvent(prev.drops, event) }));
        }
        scheduleRefresh();
      },
    });
    return () => conn.disconnect();
  }, [brand.id, scheduleRefresh]);

  // Polling while the socket is down: 5 s while a chest is out there, 30 s
  // otherwise; paused while the tab is in the background.
  const hasActive = active.length > 0;
  useEffect(() => {
    if (connected) return undefined;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      refresh();
    }, pollIntervalMs(hasActive));
    return () => clearInterval(id);
  }, [connected, hasActive, refresh]);

  // Read again just after the first chest leaves for good (the server closes it then).
  const nextExpiry = active.reduce(
    (min, d) => (d.expiresAt != null && (min === null || d.expiresAt < min) ? d.expiresAt : min),
    null
  );
  useEffect(() => {
    if (nextExpiry === null) return undefined;
    const wait = Math.max(500, nextExpiry - (Date.now() + offsetRef.current) + EXPIRY_RECHECK_MS);
    const id = setTimeout(refresh, wait);
    return () => clearTimeout(id);
  }, [nextExpiry, refresh]);

  const clientNow = useNow(hasActive ? 250 : null);
  const serverNow = clientNow + offsetRef.current;

  async function loadMore() {
    if (!history.nextBefore || loadingMore) return;
    setLoadingMore(true);
    try {
      const list = await fetchGiftDrops(brand.id, { limit: HISTORY_PAGE_SIZE, before: history.nextBefore });
      if (!alive.current) return;
      setHistory((prev) => {
        const ids = new Set(prev.drops.map((d) => d.id));
        return { drops: [...prev.drops, ...list.drops.filter((d) => !ids.has(d.id))], nextBefore: list.nextBefore };
      });
      setHistoryError(null);
    } catch (err) {
      if (alive.current) setHistoryError(err);
    } finally {
      if (alive.current) setLoadingMore(false);
    }
  }

  function onDropped({ drop, serverNowMs }) {
    setDropOpen(false);
    setNotice(t('gifts.dropped'));
    if (serverNowMs != null) offsetRef.current = serverNowMs - Date.now();
    if (drop && drop.status === 'active') {
      setActive((prev) => sortNewestFirst([drop, ...prev.filter((d) => d.id !== drop.id)]));
    }
    refresh();
  }

  function onCancelled(drop) {
    setCancelTarget(null);
    setNotice(t('gifts.cancelled'));
    setActive((prev) => prev.filter((d) => d.id !== drop.id));
    refresh();
  }

  const closeDrop = useCallback(() => setDropOpen(false), []);
  const closeCancel = useCallback(() => setCancelTarget(null), []);

  return (
    <div className="panel-card gifts">
      <div className="panel-card__row">
        <div>
          <h2 className="panel-card__title">{t('gifts.title')}</h2>
          <p className="gifts__lead">{t('gifts.lead')}</p>
        </div>
        {canEdit && (
          <button type="button" className="btn-o btn-o--brand" onClick={() => { setNotice(''); setDropOpen(true); }}>
            <Gift size={15} style={{ marginRight: 8 }} />
            {t('gifts.dropButton')}
          </button>
        )}
      </div>

      {notice && <div className="gifts__notice" role="status">{notice}</div>}

      <div className="gifts__subhead">
        <span>{t('gifts.activeTitle')}</span>
        <span className="gifts__count">{active.length}</span>
        <span
          className={`gifts__poll${connected ? ' is-live' : ''}`}
          title={connected ? t('gifts.liveTitle') : t('gifts.pollingTitle')}
        >
          <span className="gifts__poll-dot" aria-hidden="true" />
          {connected ? t('gifts.live') : t('gifts.polling', { s: pollIntervalMs(hasActive) / 1000 })}
        </span>
      </div>

      {activeError ? (
        <div className="gifts__error" role="alert">
          <ExclamationTriangleFill size={14} /> {errorText(activeError, t)}
          <button type="button" className="gifts__link" onClick={refresh}>{t('gifts.retry')}</button>
        </div>
      ) : !activeLoaded ? (
        <div className="panel-card__muted">{t('shell.loading')}</div>
      ) : active.length === 0 ? (
        <div className="gifts__empty">
          <Gift size={22} aria-hidden="true" />
          <span>{t('gifts.noActive')}</span>
        </div>
      ) : (
        <div className="gifts__cards">
          {active.map((drop) => (
            <ActiveCard key={drop.id} drop={drop} brand={brand} nowMs={serverNow} canEdit={canEdit} onCancel={setCancelTarget} />
          ))}
        </div>
      )}

      <div className="gifts__subhead gifts__subhead--history">
        <span>{t('gifts.historyTitle')}</span>
      </div>

      {historyError && (
        <div className="gifts__error" role="alert">
          <ExclamationTriangleFill size={14} /> {errorText(historyError, t)}
          <button type="button" className="gifts__link" onClick={refresh}>{t('gifts.retry')}</button>
        </div>
      )}
      {!historyLoaded ? (
        <div className="panel-card__muted">{t('shell.loading')}</div>
      ) : history.drops.length === 0 ? (
        !historyError && <div className="panel-card__muted">{t('gifts.noHistory')}</div>
      ) : (
        <div className="gifts__table-wrap">
          <table className="gifts__table">
            <thead>
              <tr>
                <th>{t('gifts.col.createdAt')}</th>
                <th>{t('gifts.col.status')}</th>
                <th>{t('gifts.col.prize')}</th>
                <th>{t('gifts.col.winner')}</th>
                <th className="is-num">{t('gifts.col.reaction')}</th>
                <th className="is-num">{t('gifts.col.crossing')}</th>
              </tr>
            </thead>
            <tbody>
              {history.drops.map((d) => (
                <tr key={d.id}>
                  <td>
                    <div className="gift-nowrap">{fmtDateTime(d.createdAt, lang)}</div>
                    {d.note && <div className="gifts__row-note" title={d.note}>{d.note}</div>}
                  </td>
                  <td>
                    <div className="gifts__status-cell">
                      <StatusBadge status={d.status} />
                      <FollowersBadge drop={d} />
                    </div>
                  </td>
                  <td><PrizeLine drop={d} compact /></td>
                  <td><WinnerCell drop={d} /></td>
                  <td className="is-num gift-nowrap">{d.status === 'collected' ? formatReaction(d.reactionMs, lang) : '—'}</td>
                  <td className="is-num gift-nowrap">
                    {d.crossingMs != null ? t('gifts.seconds', { n: Math.round(d.crossingMs / 1000) }) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {history.nextBefore && (
        <div className="gifts__more">
          <button type="button" className="btn-o btn-o--ghost btn-o--sm" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? t('shell.loading') : t('gifts.loadMore')}
          </button>
        </div>
      )}

      {dropOpen && <DropModal brand={brand} onClose={closeDrop} onDone={onDropped} />}
      {cancelTarget && (
        <CancelModal brand={brand} drop={cancelTarget} onClose={closeCancel} onDone={onCancelled} />
      )}
    </div>
  );
}
